import {
  ApplicationConfig,
  linkedSignal,
  LOCALE_ID,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter, TitleStrategy } from '@angular/router';
import { environment } from '../environments/environment';
import { platform } from '../platforms/platform';
import {
  createApplicationRoutes,
  provideFramework,
  provideSoftappsCore,
} from '@configs/common';
import { provideHagebauPartnerSoftlineModules } from '@configs/hagebau-partner';
import {
  lagerCoreProviders,
  SOFTLINE_CONFIG_LAGERPLATZ_AUSWAHL,
} from '@softapps/lager/core';
import { palettenUmlagerungRoutes } from '@softapps/lager/paletten-umlagerung';
import {
  lagerplatzUmlagerungProviders,
  lagerplatzUmlagerungRoutes,
} from '@softapps/lager/lagerplatz-umlagerung';
import {
  lagerkorrekturProviders,
  lagerkorrekturRoutes,
} from '@softapps/lager/lagerkorrektur';
import { inventurProviders, inventurRoutes } from '@softapps/lager/inventur';
import { lagerInventurRoutes as mdeInventurRoutes } from '@softapps/mde/inventur';
import {
  kommissionierungProviders,
  kommissionierungRoutes,
  provideVorKommissionierung,
  vorKommissionierungRoutes,
} from '@softapps/lager/kommissionierung';
import { lagerstandProviders, lagerstandRoutes } from '@softapps/lager/lagerstand';
import {
  lagerkorrekturArtikelProviders,
  lagerkorrekturArtikelRoutes,
} from '@softapps/lager/lagerkorrektur-artikel';
import {
  provideWarenuebernahmeModule,
  warenuebernahmeRoutes,
} from '@softapps/wws/warenuebernahme';
import { provideWwsCoreItemSearchConfig, wwsCoreProviders } from '@softapps/wws/core';
import { fibuCoreProviders, kundeninfoLeisteProviders } from '@softapps/fibu/core';
import {
  dashboardProviders,
  dashboardRoutes,
  provideKundeninfoWidget,
} from '@softapps/allgemein/dashboard';
import {
  BelegDownloadStrategy,
  gesamtabfrageProviders,
  gesamtabfrageRoutes,
  SOFTLINE_CONFIG_GESAMTABFRAGE,
} from '@softapps/wws/gesamtabfrage';
import { artikelInfoProviders, artikelInfoRoutes } from '@softapps/wws/artikel-info';
import { artikelvorratProviders, artikelvorratRoutes } from '@softapps/mde/artikelvorrat';
import {
  bestellvorschlagProviders,
  bestellvorschlagRoutes,
} from '@softapps/mde/bestellvorschlag';
import {
  bestellerfassungProviders,
  bestellerfassungRoutes,
} from '@softapps/mde/bestellerfassung';
import {
  etikettendruckProviders,
  etikettendruckRoutes,
} from '@softapps/mde/etikettendruck';
import { retourenProviders, retourenRoutes } from '@softapps/mde/retouren';
import {
  warenverprobungProviders,
  warenverprobungRoutes,
} from '@softapps/mde/warenverprobung';
import { statusabfrageProviders, statusabfrageRoutes } from '@softapps/pms/statusabfrage';
import {
  stempelerfassungProviders,
  stempelerfassungRoutes,
} from '@softapps/pms/stempelerfassung';
import { lieferscheinProviders, lieferscheinRoutes } from '@softapps/wws/lieferschein';
import { checklisteProviders, checklisteRoutes } from '@softapps/allgemein/checkliste';
import { freigabeProviders } from '@softapps/fibu/freigabe';
import { kreditlimitProviders } from '@softapps/wws/kreditlimit';
import {
  umsatzstatistikProviders,
  umsatzstatistikRoutes,
} from '@softapps/wws/umsatzstatistik';
import { haftruecklassProviders } from '@softapps/fibu/haftruecklass';
import { offenePostenProviders } from '@softapps/fibu/offene-posten';
import { kundenabfrageProviders, kundenabfrageRoutes } from '@softapps/wws/kundenabfrage';
import { artikelstammRoutes, provideWwsArtikelstamm } from '@softapps/wws/artikelstamm';
import {
  provideStorageLocation,
  storageLocationRoutes,
} from '@softapps/mde/storage-location';
import { mdeInventoryRoutes } from '../../../../../libs/softapps/mde/inventory/src/lib/inventory.routes';
import { provideMdeInventoryModule } from '../../../../../libs/softapps/mde/inventory/src/lib/inventory.providers';
import {
  palettenkorrekturRoutes,
  providePalettenkorrektur,
} from '@softapps/lager/palettenkorrektur';
import {
  provideDarkTheme,
  remoteConfigProviders,
  SOFTLINE_CONFIG_LANGUAGES,
  SOFTLINE_LANGUAGE_DE,
  SOFTLINE_LANGUAGE_EN,
  SoftlineTitleStrategy,
  TitleService,
} from '@softline/application';
import {
  productRelocationRoutes,
  providePalletRelocationModule,
} from '@softapps/lager/product-relocation';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import {
  provideSoftApps,
  SOFTLINE_CONFIG_PUBLIC_SHELL_BRANDING,
  SOFTLINE_ROUTES_APPS,
  SOFTLINE_ROUTES_EMBEDDED,
  SOFTLINE_ROUTES_PUBLIC,
  SOFTLINE_ROUTES_TERMINAL,
  withTerminalAutoLogin,
} from '@softapps/core';
import { mdcInventoryRouteFactory, provideMdcInventory } from '@softapps/mdc/inventory';
import { moduleRoutes, provideModuleOverview } from '@softapps/allgemein/modules';
import { loginRoutes, withLogin } from '@softapps/allgemein/login';
import {
  provideNativeTooltipOverride,
  SOFTLINE_CONFIG_MESSAGE_BAR_DURATION,
} from '@softline/ui-core';
import { Title } from '@angular/platform-browser';
import { registerLocaleData } from '@angular/common';
import { default as localeDe } from '@angular/common/locales/de';
import {
  accountContextProviders,
  connectionSettingsRoutes,
  contextSettingsProviders,
  settingsProviders,
  settingsRoutes,
  userContextProviders,
  utilitiesProviders,
  vkforgContextProviders,
} from '@softapps/allgemein/utils';
import { passwordProviders, passwordRoutes } from '@softapps/allgemein/password';
import {
  SOFTLINE_CONFIG_AUTHENTICATION_FORWARD_ROUTE,
  SOFTLINE_CONFIG_JWT_EXPIRATION_TIME,
  SOFTLINE_CONFIG_JWT_REFRESH_TIME,
} from '@softline/auth';

registerLocaleData(localeDe, 'de');

const appName = linkedSignal(() => 'Holz Braun');

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter([]),
    provideHttpClient(withInterceptorsFromDi()),
    provideZoneChangeDetection({ eventCoalescing: true }),

    {
      provide: SOFTLINE_ROUTES_APPS,
      useValue: [
        ...gesamtabfrageRoutes,
        ...artikelInfoRoutes,
        ...bestellvorschlagRoutes,
        ...bestellerfassungRoutes,
        ...etikettendruckRoutes,
        ...retourenRoutes,
        ...warenverprobungRoutes,

        ...artikelvorratRoutes,
        ...warenuebernahmeRoutes,

        ...stempelerfassungRoutes,
        ...statusabfrageRoutes,
        ...lieferscheinRoutes,
        ...umsatzstatistikRoutes,
        ...checklisteRoutes,

        ...dashboardRoutes,
        ...kundenabfrageRoutes,

        ...moduleRoutes,
        ...settingsRoutes,
        ...palettenUmlagerungRoutes,
        ...lagerplatzUmlagerungRoutes,
        ...lagerkorrekturRoutes,
        ...inventurRoutes,
        ...mdeInventurRoutes,
        ...kommissionierungRoutes,
        ...lagerstandRoutes,
        ...lagerkorrekturArtikelRoutes,
        ...artikelstammRoutes,
        ...storageLocationRoutes,
        ...productRelocationRoutes,

        ...mdeInventoryRoutes,
        ...palettenkorrekturRoutes,
        ...vorKommissionierungRoutes,
        ...mdcInventoryRouteFactory({}),
      ],
      multi: true,
    },
    {
      provide: SOFTLINE_ROUTES_TERMINAL,
      useValue: [],
      multi: true,
    },
    {
      provide: SOFTLINE_ROUTES_PUBLIC,
      useValue: [
        ...loginRoutes,
        ...passwordRoutes,
        ...connectionSettingsRoutes,
        ...settingsRoutes,
      ],
      multi: true,
    },
    {
      provide: SOFTLINE_ROUTES_EMBEDDED,
      useValue: [...moduleRoutes, ...statusabfrageRoutes, ...dashboardRoutes],
      multi: true,
    },

    provideSoftApps(
      { connection: environment.connection },
      withLogin()
      //withRegistration()
    ),
    provideDarkTheme(),
    provideNativeTooltipOverride(),
    {
      provide: TitleStrategy,
      useFactory: (title: Title, service: TitleService) =>
        new SoftlineTitleStrategy(appName(), title, service),
      deps: [Title, TitleService],
    },
    {
      provide: SOFTLINE_CONFIG_LANGUAGES,
      useValue: [SOFTLINE_LANGUAGE_DE, SOFTLINE_LANGUAGE_EN],
    },

    ...userContextProviders,
    ...remoteConfigProviders,
    ...contextSettingsProviders,
    ...accountContextProviders,
    ...vkforgContextProviders,
    ...utilitiesProviders,
    ...settingsProviders,
    ...passwordProviders,
    provideModuleOverview({
      preserveProviderOrder: true,
    }),

    //Wws
    provideWwsCoreItemSearchConfig({ useMatchcodeAsTitle: true }),
    ...wwsCoreProviders,
    ...fibuCoreProviders,
    ...kundeninfoLeisteProviders,
    ...provideKundeninfoWidget(),
    ...dashboardProviders,

    // Lager
    ...lagerCoreProviders,
    provideVorKommissionierung(),
    ...kommissionierungProviders,

    provideWarenuebernahmeModule({
      dierektannahme: true,
      reklamation: true,
      lagerplatzauswahl: true,
      ekBarcode: true,
    }),

    ...providePalletRelocationModule(),
    ...lagerplatzUmlagerungProviders,

    ...lagerkorrekturProviders,
    providePalettenkorrektur(),
    ...lagerkorrekturArtikelProviders,
    ...inventurProviders,
    provideMdcInventory(),
    ...lagerstandProviders,
    ...artikelInfoProviders,
    ...provideMdeInventoryModule(),

    ...provideStorageLocation(),

    ...provideWwsArtikelstamm({
      readonly: false,
      hasProductDescriptionFeature: true,
    }),

    // Gesamtabfrage (Belege)
    ...gesamtabfrageProviders,
    {
      provide: SOFTLINE_CONFIG_GESAMTABFRAGE,
      useValue: {
        geschaeftsfall: true,
        download: [
          {
            belegart: 'Rechnung',
            strategies: [BelegDownloadStrategy],
          },
          {
            belegart: 'Lieferschein',
            strategies: [BelegDownloadStrategy],
          },
          {
            belegart: 'Auftrag',
            strategies: [BelegDownloadStrategy],
          },
          {
            belegart: 'Offert',
            strategies: [BelegDownloadStrategy],
          },
          {
            belegart: 'Kommissionierschein',
            strategies: [BelegDownloadStrategy],
          },
        ],
      },
    },

    // MDE Module
    ...artikelvorratProviders,
    ...bestellvorschlagProviders,
    ...bestellerfassungProviders,
    ...etikettendruckProviders,
    ...provideMdeInventoryModule(),
    ...retourenProviders,
    ...warenverprobungProviders,

    // PMS
    ...statusabfrageProviders,
    ...stempelerfassungProviders,

    ...lieferscheinProviders,
    ...checklisteProviders,

    ...freigabeProviders,
    ...kreditlimitProviders,
    ...umsatzstatistikProviders,
    ...haftruecklassProviders,
    ...offenePostenProviders,
    ...kundenabfrageProviders,

    ...platform.providers,
    { provide: LOCALE_ID, useValue: 'de' },
    { provide: SOFTLINE_CONFIG_LAGERPLATZ_AUSWAHL, useValue: 'scanAndSelect' },
    {
      provide: SOFTLINE_CONFIG_AUTHENTICATION_FORWARD_ROUTE,
      useValue: '/',
    },
    {
      provide: SOFTLINE_CONFIG_MESSAGE_BAR_DURATION,
      useValue: 5000,
    },
    {
      provide: SOFTLINE_CONFIG_JWT_EXPIRATION_TIME,
      useValue: 604_800, // 7 days
    },
    {
      provide: SOFTLINE_CONFIG_JWT_REFRESH_TIME,
      useValue: 172_800, // 2 days
    },

    { provide: SOFTLINE_CONFIG_PUBLIC_SHELL_BRANDING, useValue: true },
  ],
};
